import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtCurrency } from "@/lib/format";
import { Loader2 } from "lucide-react";

const EMPTY = { parent: "", child: "", date: "", ratio: "", cost_pct: "" };

function Field({ label, children, hint }) {
    return (
        <div className="space-y-1">
            <Label className="text-xs">{label}</Label>
            {children}
            {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
        </div>
    );
}

function PlanTable({ plan, shares, setShares }) {
    return (
        <table className="w-full text-xs" data-testid="spinoff-plan">
            <thead className="text-muted-foreground text-[10px] uppercase tracking-wider">
                <tr><th className="text-left py-1">Sub-portfolio</th><th className="text-right">{plan.parent} held</th><th className="text-right">Cost basis</th><th className="text-right">New {plan.child} shares</th><th className="text-right">Cost moved</th></tr>
            </thead>
            <tbody>
                {plan.subs.map((s) => (
                    <tr key={s.sub} className="border-t border-border/50">
                        <td className="py-1.5">{s.sub}</td>
                        <td className="text-right num">{s.parent_shares}</td>
                        <td className="text-right num">{fmtCurrency(s.parent_cost)}</td>
                        <td className="text-right">
                            <Input value={shares[s.sub] ?? s.child_shares} onChange={(e) => setShares({ ...shares, [s.sub]: e.target.value })}
                                title="Type 0 to leave this sub-portfolio out"
                                className="h-7 w-[100px] ml-auto text-xs num text-right" data-testid={`spinoff-shares-${s.sub}`} />
                        </td>
                        <td className="text-right num">{fmtCurrency(s.moved_cost)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

export function SpinoffDialog({ open, onOpenChange, prefill, onSaved }) {
    const [f, setF] = useState(EMPTY);
    const [plan, setPlan] = useState(null);
    const [shares, setShares] = useState({});
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setF({ ...EMPTY, ...(prefill || {}), child: prefill?.child || "", ratio: prefill?.ratio ? String(+prefill.ratio.toFixed(6)) : "" });
        setPlan(null); setShares({});
    }, [open, prefill]);

    const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); if (k !== "cost_pct") setPlan(null); };
    const ready = f.parent && f.child && f.date && Number(f.ratio) > 0;
    const stale = !!plan && String(plan.cost_pct ?? "") !== String(f.cost_pct).trim();

    const preview = async (keepPct) => {
        setBusy(true);
        try {
            const { data } = await api.post("/corporate-actions/spinoff/preview", { ...f, cost_pct: keepPct ? f.cost_pct : null });
            setPlan(data);
            if (!keepPct) setF((x) => ({ ...x, cost_pct: data.cost_pct != null ? String(data.cost_pct) : "" }));
        } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
    };
    const save = async () => {
        setBusy(true);
        try {
            const subs = Object.entries(shares).filter(([, v]) => String(v).trim() !== "").map(([sub, v]) => ({ sub, child_shares: Number(v) }));
            const { data } = await api.post("/corporate-actions/spinoff", { ...f, subs });
            toast.success(data.message);
            onSaved?.(); onOpenChange(false);
        } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="spinoff-dialog">
                <DialogHeader>
                    <DialogTitle>Record a spin-off</DialogTitle>
                    <DialogDescription>Adds the new company's shares to every sub-portfolio that held the parent, and moves part of the parent's cost basis to them. No cash moves and no gain is booked.</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Field label="Parent ticker"><Input value={f.parent} onChange={(e) => set("parent", e.target.value.toUpperCase().trim())} className="num uppercase" data-testid="spinoff-parent-input" /></Field>
                    <Field label="New company ticker"><Input value={f.child} onChange={(e) => set("child", e.target.value.toUpperCase().trim())} className="num uppercase" data-testid="spinoff-child-input" /></Field>
                    <Field label="Ex-date"><Input type="date" value={f.date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => set("date", e.target.value)} className="num" data-testid="spinoff-date-input" /></Field>
                    <Field label="New shares per parent share"><Input value={f.ratio} onChange={(e) => set("ratio", e.target.value)} placeholder="e.g. 0.3333" className="num" data-testid="spinoff-ratio-input" /></Field>
                </div>
                {plan && (
                    <div className="space-y-3">
                        <Field label="Share of cost basis moved to the new company (%)"
                            hint={plan.suggested_cost_pct != null
                                ? `Pre-filled from first-day closes: ${plan.parent} ${fmtCurrency(plan.parent_close)}, ${plan.child} ${fmtCurrency(plan.child_close)} → ${plan.suggested_cost_pct}%. Use your broker's or the company's tax-basis figure if it differs.`
                                : "No first-day prices available, so enter the percentage from the company's tax-basis notice."}>
                            <div className="flex gap-2">
                                <Input value={f.cost_pct} onChange={(e) => set("cost_pct", e.target.value)} className="w-[120px] num" data-testid="spinoff-pct-input" />
                                <Button variant="outline" size="sm" onClick={() => preview(true)} disabled={busy} data-testid="spinoff-recalc-btn">Recalculate</Button>
                            </div>
                        </Field>
                        <PlanTable plan={plan} shares={shares} setShares={setShares} />
                    </div>
                )}
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="spinoff-cancel-btn">Cancel</Button>
                    {!plan
                        ? <Button onClick={() => preview(false)} disabled={!ready || busy} data-testid="spinoff-preview-btn">{busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Preview</Button>
                        : <Button onClick={save} disabled={busy || f.cost_pct === "" || stale} data-testid="spinoff-save-btn">{busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}{stale ? "Recalculate first" : "Record spin-off"}</Button>}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
